export type NotificationSetup = "ready" | "install-ios" | "unsupported" | "denied" | "available" | "unconfigured" | "error";

export interface NotificationState {
  setup: NotificationSetup;
  detail: string;
  subscribed: boolean;
}

function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function standalone(): boolean {
  return matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function supported(): boolean {
  return isSecureContext && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function decodeKey(value: string): Uint8Array {
  const padded = value + "=".repeat((4 - value.length % 4) % 4);
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

async function serverState(): Promise<{ configured: boolean; publicKey: string | null }> {
  const response = await fetch("/api/push/state", { cache: "no-store" });
  if (!response.ok) throw new Error("Notification service is unavailable.");
  return response.json();
}

export async function notificationState(): Promise<NotificationState> {
  if (isIOS() && !standalone()) return { setup: "install-ios", detail: "On iPhone, tap Share, choose Add to Home Screen, then open Time Tile from its icon.", subscribed: false };
  if (!supported()) return { setup: "unsupported", detail: "This browser cannot receive Time Tile notifications.", subscribed: false };
  if (Notification.permission === "denied") return { setup: "denied", detail: "Notifications are blocked. Allow Time Tile in this device’s notification settings, then return here.", subscribed: false };
  try {
    const [server, registration] = await Promise.all([serverState(), navigator.serviceWorker.ready]);
    if (!server.configured || !server.publicKey) return { setup: "unconfigured", detail: "The notification service still needs its server key.", subscribed: false };
    const subscription = await registration.pushManager.getSubscription();
    if (Notification.permission === "granted" && subscription) return { setup: "ready", detail: "This device is ready for quiet check-ins.", subscribed: true };
    return { setup: "available", detail: "Enable notifications so Time Tile can ask about uncovered time while it is closed.", subscribed: false };
  } catch (error) {
    return { setup: "error", detail: error instanceof Error ? error.message : "Could not check notifications.", subscribed: false };
  }
}

export async function enableNotifications(): Promise<void> {
  if (!supported()) throw new Error("Notifications are not supported on this device.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notifications were not allowed.");
  const server = await serverState();
  if (!server.publicKey) throw new Error("The notification service is not configured.");
  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: decodeKey(server.publicKey) as BufferSource });
  const response = await fetch("/api/push/subscriptions", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(subscription) });
  if (!response.ok) throw new Error("This device could not be registered.");
}

export async function sendTestNotification(): Promise<void> {
  const response = await fetch("/api/push/test", { method: "POST", headers: { "content-type": "application/json" }, body: "{}" });
  if (!response.ok) throw new Error("The test notification could not be sent.");
}

export async function showReviewNotification(body: string): Promise<boolean> {
  if (!supported() || Notification.permission !== "granted") return false;
  const registration = await navigator.serviceWorker.ready;
  await registration.showNotification("Time Tile", {
    body,
    tag: "time-tile-current-review",
    icon: "/icon.svg",
    data: { url: "/" },
  });
  return true;
}
